import React, {
  createContext, useContext, useState, useCallback, useEffect, useMemo,
} from 'react';
import { toast } from 'sonner';
import { adminFetch } from '@/utils/adminAuth';
import { useAuth } from '@/context/AuthContext';

/**
 * Per-page inline edit context.
 *
 * Unlike the site-wide `EditModeContext` (homepage only), this provider is
 * mounted at each page that wants inline editing.  It fetches
 * `/api/page-content/{slug}` on enter, keeps a local draft, and PATCHes
 * `/api/admin/page-content/{slug}` on save.
 *
 * Usage:
 *   <PageEditProvider slug="registration">
 *     <RegistrationPage />
 *     <PageEditToolbar />
 *   </PageEditProvider>
 *
 *   // Inside RegistrationPage:
 *   const { getValue } = usePageEditMode();
 *   <Editable path="hero.title" fallback="Mardi Gras Brasil…" />
 */
const PageEditContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

// Deep get/set helpers — support dotted paths and numeric indices.
const getAt = (obj, path) => {
  if (!obj) return undefined;
  return path.split('.').reduce((acc, part) => {
    if (acc === undefined || acc === null) return acc;
    if (Array.isArray(acc)) return acc[Number(part)];
    return acc[part];
  }, obj);
};

const setAt = (obj, path, value) => {
  const parts = path.split('.');
  const clone = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  let cur = clone;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    const next = Array.isArray(cur[p]) ? [...cur[p]] : { ...(cur[p] || {}) };
    cur[p] = next;
    cur = next;
  }
  cur[parts[parts.length - 1]] = value;
  return clone;
};

export const PageEditProvider = ({ slug, group, children }) => {
  const { isAdmin } = useAuth();
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;

  const [enabled, setEnabled] = useState(false);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [published, setPublished] = useState({}); // server copy shown when not editing

  // Load the published content once per slug so <Editable> shows overrides
  // even when we're not in edit mode.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/page-content/${slug}`);
        const data = await r.json();
        if (!cancelled) setPublished(data.content || {});
      } catch {
        /* ignore — page will render its hard-coded fallbacks */
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug]);

  // Warn if leaving with unsaved changes.
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  // In edit mode, block <a> navigation only on editable text (keeps the caret in place)
  // or while there are unsaved edits; every other link keeps working.
  useEffect(() => {
    if (!enabled) return undefined;
    document.body.classList.add('cs-editing');
    const clickCapture = (e) => {
      const t = e.target;
      if (!t || typeof t.closest !== 'function') return;
      if (t.closest('[data-edit-safe]')) return;
      const anchor = t.closest('a');
      if (!anchor) return;
      if (t.closest('[data-edit-path]')) {
        e.preventDefault();
      } else if (dirty) {
        e.preventDefault();
        toast.info('Save or cancel your edits before leaving this page.');
      }
    };
    document.addEventListener('click', clickCapture, true);
    return () => {
      document.body.classList.remove('cs-editing');
      document.removeEventListener('click', clickCapture, true);
    };
  }, [enabled, dirty]);

  const enter = useCallback(async (opts) => {
    try {
      const r = await fetch(`${API}/api/page-content/${slug}`);
      const data = await r.json();
      setDraft(data.content || {});
      setDirty(false);
      setEnabled(true);
      if (group && !opts?.silent) {
        window.dispatchEvent(new CustomEvent('page-edit-group', { detail: { group, action: 'enter', from: slug } }));
      }
    } catch (e) {
      toast.error('Failed to enter edit mode');
    }
  }, [slug, group]);

  const cancel = useCallback((opts) => {
    if (!opts?.silent && dirty && !window.confirm('Discard your unsaved changes?')) return;
    setEnabled(false);
    setDraft(null);
    setDirty(false);
    if (group && !opts?.silent) {
      window.dispatchEvent(new CustomEvent('page-edit-group', { detail: { group, action: 'cancel', from: slug } }));
    }
  }, [dirty, slug, group]);

  const update = useCallback((path, value) => {
    setDraft((prev) => setAt(prev || {}, path, value));
    setDirty(true);
  }, []);

  const save = useCallback(async (opts) => {
    if (!draft) {
      // Nothing to save on this provider — still broadcast so peers save too.
      if (group && !opts?.silent) {
        window.dispatchEvent(new CustomEvent('page-edit-group', { detail: { group, action: 'save', from: slug } }));
      }
      return;
    }
    setSaving(true);
    try {
      const res = await adminFetch(`/admin/page-content/${slug}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: draft }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      const data = await res.json();
      setPublished(data.content || draft);
      if (!opts?.silent) toast.success('Saved!');
      setDirty(false);
      setEnabled(false);
      if (group && !opts?.silent) {
        window.dispatchEvent(new CustomEvent('page-edit-group', { detail: { group, action: 'save', from: slug } }));
      }
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [draft, slug, group]);

  // Listen to shared-group events so a "peer" provider can drive us too.
  useEffect(() => {
    if (!group) return undefined;
    const onGroup = (e) => {
      const d = e.detail || {};
      if (d.group !== group || d.from === slug) return;
      if (d.action === 'enter') enter({ silent: true });
      else if (d.action === 'save') save({ silent: true });
      else if (d.action === 'cancel') cancel({ silent: true });
    };
    window.addEventListener('page-edit-group', onGroup);
    return () => window.removeEventListener('page-edit-group', onGroup);
  }, [group, slug, enter, save, cancel]);

  const value = useMemo(() => ({
    slug,
    enabled,
    draft,
    dirty,
    saving,
    isAdminUser,
    enter,
    cancel,
    save,
    update,
    // getValue returns the DRAFT when in edit mode, else the PUBLISHED value.
    getValue: (path) => getAt(enabled ? draft : published, path),
  }), [slug, enabled, draft, dirty, saving, isAdminUser, enter, cancel, save, update, published]);

  return <PageEditContext.Provider value={value}>{children}</PageEditContext.Provider>;
};

export const usePageEditMode = () => {
  const ctx = useContext(PageEditContext);
  if (!ctx) {
    // Safe no-op default so <PageEditable> can render outside a provider
    // without crashing (e.g. during Storybook rendering).
    return {
      enabled: false, isAdminUser: false, saving: false,
      getValue: () => undefined,
      update: () => {},
      enter: () => {}, cancel: () => {}, save: () => {},
    };
  }
  return ctx;
};
